import * as v3d from 'verge3d';
import { create } from 'zustand';

export const DEFAULT_FEATURE_ENABLED = {
    animate: false,
    background: true,
    backgroundColor: true,
    centerModel: true,
    tableOfContent: true,
    draw: true,
    fullScreen: true,
    info: true,
    labels: true,
    labelsUi: true,
    layers: true,
    play: true,
    screenRecord: true,
    screenShot: true,
    showFeedback: true,
    zoomControls: true,
};

const useLayersStore = create((set, get) => ({
    infoOverlayOpened: false,
    setInfoOverlayOpened: infoOverlayOpened => set({ infoOverlayOpened }),
    activeLayerIndex: 0,
    animationAudios: [],
    setAnimationAudios: audios => set({ animationAudios: audios }),
    setActiveLayerIndex: index => {
        console.log('increase', index);
        const validIndex = Math.max(0, Math.min(index, get().layers?.length - 1 || 0));
        set(state => {
            return { activeLayerIndex: validIndex };
        });
        // Call changeLayer with new index
        get().changeLayer(validIndex);
    },
    layers: Array.from({ length: 10 }, (_, i) => i),
    setLayers: layers => set({ layers }),
    v3dApp: null,
    setV3dApp: app => set({ v3dApp: app }),
    layerPrefix: '',
    setLayerPrefix: prefix => set({ layerPrefix: prefix }),
    biocloudGeneratorConfig: null,
    setBiocloudGeneratorConfig: config => set({ biocloudGeneratorConfig: config }),
    changeLayer: layer => {
        const v3dApp = get().v3dApp;
        if (!v3dApp) return;

        v3dApp.scene.traverse(function(obj) {
            const groupNames = obj.groupNames;
            if (!groupNames) return;

            for (let i = 0; i < groupNames.length; i++) {
                const groupName = groupNames[i];
                if (!groupName.includes(get().layerPrefix)) return;
                const groupNumber = parseInt(groupName.replace(get().layerPrefix, ''));

                if (groupNumber <= layer) {
                    obj.visible = false;
                } else {
                    obj.visible = true;
                }
            }
        });
    },
    animations: [],
    annotations: [],
    animationInfos: [],
    currentAnimation: null,
    lastSelectedAnnotation: null,
    setAnnotations: annotations => set({ annotations }),
    setAnimations: animations => set({ animations }),
    setAnimationInfos: animationInfos => set({ animationInfos }),
    setCurrentAnimation: animation => {
        if (animation) set({ lastSelectedAnimation: animation });
        set({ currentAnimation: animation });
    },
    annotationsEnabled: true,
    setAnnotationsEnabled: enabled => set({ annotationsEnabled: enabled }),
    fitScene: () => {
        const camera = get().v3dApp.camera;

        const zoom = [camera.zoom];

        const pos = new v3d.Vector3(),
            target = new v3d.Vector3();
        v3d.CameraUtils.calcCameraZoomToObjectsParams(
            camera,
            get().v3dApp.scene.children,
            pos,
            target,
            zoom
        );

        if (get().v3dApp.controls && !get().v3dApp.controls.inTween) {
            if (get().v3dApp.camera.isOrthographicCamera) {
                get().v3dApp.controls.tweenZoomTo(target, zoom[0], duration, doSlot);
            } else {
                get().v3dApp.controls.tween(pos, target, 0, () => { });
            }
        }
    },
    currentTool: 'brush',
    setCurrentTool: tool => set({ currentTool: tool }),
    currentColor: '#000000',
    setCurrentColor: color => set({ currentColor: color }),
    brushSize: 8,
    setBrushSize: sizeCB => {
        set({ brushSize: sizeCB(get().brushSize) });
    },
    lines: [],
    setLines: lines => set({ lines }),
    clearCanvas: () => {
        set({ lines: [] });
        set({ textNodes: [] });
        set({ isDrawingMode: false })
        setTimeout(() => set({ isDrawingMode: true }))
    },
    textNodes: [],
    addTextNode: newNode =>
        set(state => {
            const nodeWithDefaults = {
                rotation: 0,
                scaleX: 1,
                scaleY: 1,
                ...newNode,
            };

            const existingIndex = state.textNodes.findIndex(node => node.id === nodeWithDefaults.id);
            if (existingIndex !== -1) {
                const updatedNodes = [...state.textNodes];
                updatedNodes[existingIndex] = nodeWithDefaults;
                return { textNodes: updatedNodes };
            }
            return { textNodes: [...state.textNodes, nodeWithDefaults] };
        }),
    isDrawingMode: false,
    setIsDrawingMode: enabled => set({ isDrawingMode: enabled }),
    isExDrawingMode: false,
    setIsExDrawingMode: (enabled) => {
      set({ isExDrawingMode: enabled });
      // Clean up background image when disabling drawing mode
      if (!enabled) {
        set({ excalidrawBackground: null });
      }
    },
    excalidrawElements: [],
    setExcalidrawElements: elements => set({ excalidrawElements: elements }),
    excalidrawBackground: null,
    setExcalidrawBackground: background => set({ excalidrawBackground: background }),
    screenshot: null,
    setScreenshot: screenshot => set({ screenshot }),
    isLoading: true,
    loadingProgress: 0,
    setIsLoading: loading => set({ isLoading: loading }),
    setLoadingProgress: progress => set({ loadingProgress: progress }),
    branding: {
        backgroundLogo: null,
        backgroundLogoDataURL: null, // Data URL version for screenshots
        loadingLogo: null,
        primaryColor: null,
        backgroundColor: null,
    },
    setFeatureEnabled: featureEnabled => {
        console.log('featureEnabled!!!', featureEnabled);
        set({ featureEnabled });
    },
    featureEnabled: DEFAULT_FEATURE_ENABLED,
    setBranding: branding => {
        console.log('branding!!!', branding);
        set({ branding });
    },
    expandedAnnotations: new Set(),
    setExpandedAnnotation: (id, isExpanded) => {
        set(state => ({
            expandedAnnotations: isExpanded
                ? new Set([...state.expandedAnnotations, id])
                : new Set([...state.expandedAnnotations].filter(annotId => annotId !== id)),
        }))
    }
    ,
    closeAllAnnotations: () => set({ expandedAnnotations: new Set() }),
    zoomIn: () => {
        const v3dApp = get().v3dApp;
        if (v3dApp.camera) {
            const startZoom = v3dApp.camera.zoom;
            const endZoom = startZoom * 1.15;
            const duration = 100;
            const startTime = Date.now();
            
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                v3dApp.camera.zoom = startZoom + (endZoom - startZoom) * progress;
                v3dApp.camera.updateProjectionMatrix();
                if (progress < 1) requestAnimationFrame(animate);
            };
            animate();
        }
    },
    zoomOut: () => {
        const v3dApp = get().v3dApp;
        if (v3dApp.camera) {
            const startZoom = v3dApp.camera.zoom;
            const endZoom = startZoom * 0.85;
            const duration = 100;
            const startTime = Date.now();
            
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                v3dApp.camera.zoom = startZoom + (endZoom - startZoom) * progress;
                v3dApp.camera.updateProjectionMatrix();
                if (progress < 1) requestAnimationFrame(animate);
            };
            animate();
        }
    },
}));

export default useLayersStore;
